import React, { Component } from "react";
import { App } from "../../App";
import { AppScreen } from "../../AppScreen";
import landingFooterImage from "../../assets/images/landing-footer.png";
import landingSun from "../../assets/images/sun.png";
import { CloseButton } from "../components/CloseButton";
import { Quiz } from "../../modules/quiz/Quiz";
import { Question } from "../../modules/quiz/Question";
import { Answer } from "../../modules/quiz/Answer";
import { QuizCountry, getCountryNameByQuizCountry } from "../../modules/quiz/QuizCountry";
import { ConfettiAnimation } from "../components/animations/ConfettiAnimation";
import quizWellDoneText from "../../assets/images/quiz-well-done.png";
import { BirdAnimation } from "../components/animations/BirdAnimation";

export interface QuizScreenProps
{
}

interface QuizScreenState 
{
    screen: Screen;
    question: Question;
    answers: Array<JSX.Element>;
    answeredCorrect: boolean;
    currentQuestionNumber: number;
    totalQuestionNumber: number;
    showAnimation: boolean;
}

enum Screen 
{
    Quiz,
    QuizComplete
}

export class QuizScreen extends Component<QuizScreenProps, QuizScreenState>
{
    private quiz: Quiz;

    constructor(props: QuizScreenProps)
    {
        super(props);

        this.state = {
            question: null,
            answers: [],
            answeredCorrect: null,
            showAnimation: false,
            currentQuestionNumber: -1,
            totalQuestionNumber: -1,
            screen: Screen.Quiz
        };
    }

    public componentDidMount(): void
    {
        this.createQuiz();
        this.startQuiz();
    }

    private createQuiz(): void 
    {
        document.body.classList.remove("quiz-complete");

        this.quiz = App.I.quizQuestionManager.createQuiz(this.onQuizCompleted.bind(this))
    }

    private startQuiz(): void 
    {
        document.body.classList.add("quiz-active");

        this.quiz.startQuiz();
        this.setState({
            question: this.quiz.getCurrentQuestion(),
            answers: this.buildAnswers(),
            currentQuestionNumber: this.quiz.getCurrentQuestionNumber(),
            totalQuestionNumber: this.quiz.getNumberOfQuestions(),
        });
    }

    private nextQuestion(): void 
    {
        this.quiz.nextQuestion();

        this.setState({
            answers: [],
            answeredCorrect: null,
        },
            () =>
            {
                this.setState({
                    question: this.quiz.getCurrentQuestion(),
                    answers: this.buildAnswers(),
                    currentQuestionNumber: this.quiz.getCurrentQuestionNumber(),
                    totalQuestionNumber: this.quiz.getNumberOfQuestions()
                })
            }
        );
    }

    private resetQuestion(): void 
    {
        this.quiz.retryQuestion();

        this.setState({
            answers: [],
        }, () =>
        {
            this.setState({
                question: this.quiz.getCurrentQuestion(),
                answers: this.buildAnswers()
            })
        });
    }

    private buildAnswers(): Array<JSX.Element>
    {
        let answers = [];

        for (let i = 0; i < this.quiz.getCurrentQuestion().getAnswers().length; i++)
        {
            let answer: Answer = this.quiz.getCurrentQuestion().getAnswers()[i];
            let markIncorrect: boolean = this.quiz.getIncorrectSelectedAnswers().indexOf(answer) > -1;
            let showIncorrectOnly: boolean = this.state.answeredCorrect === false && markIncorrect === true;

            if (this.state.answeredCorrect)
            {
                // Shows only correct answers
                let showCorrectAnswers: boolean = this.state.answeredCorrect === true && answer.isAnswerCorrect() === true;
                answers.push(
                    <button className={"btn btn-primary" + (showCorrectAnswers === true ? " correct" : " ")}
                        onClick={this.onSubmitAnswer.bind(this, answer)} key={i}>
                        {answer.getAnswerText()}
                    </button>
                );
            }
            else 
            {
                // Shows incorrect answers from user submitted answers
                answers.push(
                    <button className={"btn btn-primary" + (showIncorrectOnly ? " incorrect" : "")}
                        onClick={this.onSubmitAnswer.bind(this, answer)} key={i}>
                        {answer.getAnswerText()}
                    </button>
                );
            }
        }

        return answers;
    }

    private onSubmitAnswer(answer: Answer): void
    {
        if (this.state.showAnimation)
            return;

        let answeredCorrect = this.quiz.checkAnswer([answer]);

        this.setState({
            answeredCorrect
        },
            () => 
            {
                if (answeredCorrect === true)
                {
                    this.onNextQuestion();
                }

                this.setState({
                    answers: this.buildAnswers()
                });
            }
        );
    }

    private onCloseClicked(): void
    {
        App.I.changeScreen(AppScreen.Landing);
    }

    private onNextQuestion(): void
    {
        if (this.state.answeredCorrect)
        {
            this.setState({
                showAnimation: true
            });
        }
        else
        {
            this.resetQuestion();
        }
    }

    private onQuizCompleted(): void 
    {
        document.body.classList.add("quiz-complete");
        document.body.classList.remove("quiz-active");

        this.setState({
            screen: Screen.QuizComplete
        });
    }

    private onPlayAgainClicked(): void 
    {
        this.createQuiz();
        this.startQuiz();
        this.setState({
            screen: Screen.Quiz
        });
    }

    public componentWillUnmount(): void
    {
        document.body.classList.remove("quiz-complete");
        document.body.classList.remove("quiz-active");
    }

    private onAnimationComplete(): void 
    {
        this.setState({
            showAnimation: false
        });
        this.nextQuestion();
    }

    public render(): JSX.Element
    {
        return (
            <>
                <CloseButton onClicked={this.onCloseClicked.bind(this)} />

                {this.state.screen === Screen.Quiz
                    ?
                    <div className={"quiz-screen " + (this.state.question?.quizIllustrationClassName)}>


                        <div className="container">
                            <div className="quiz-container">
                                <div className="quiz-header">
                                    <div className="question-number">
                                        <div className={`icon icon-country ${this.state.question?.quizCountryClassName}`}>
                                        </div>

                                        Question {this.state.currentQuestionNumber} of {this.state.totalQuestionNumber}
                                    </div>

                                    <div className="quiz-question mt-3">
                                        <p className="text-body">
                                            {this.state.question?.getQuestionText()}
                                        </p>
                                    </div>

                                    {this.state.showAnimation ?
                                        <div style={{ position: "absolute", top: 0, bottom: 0, left: 0, right: 0, zIndex: 1 }}>
                                            <ConfettiAnimation onComplete={this.onAnimationComplete.bind(this)} />
                                        </div>
                                        : null
                                    }
                                </div>

                                <div className="quiz-answers">
                                    {this.state.answers}
                                </div>
                            </div>
                        </div>
                    </div>
                    :
                    <div className="quiz-screen">
                        <div className="bird-animation">
                            {/* <BirdAnimation /> */}
                        </div>

                        <div className="container">
                            <div className="search-find-container mt-5">
                                <div className="heading-container">
                                    <img src={quizWellDoneText} alt="Well done! You completed the quiz!" />
                                </div>

                                <div className="mt-1 mb-5 ms-auto me-auto text-center">
                                    <button className="btn btn-primary" onClick={this.onPlayAgainClicked.bind(this)}>Play again</button>
                                </div>
                            </div>

                            <div className="d-flex ms-auto me-auto more">
                                <button className="btn btn-link" onClick={() => App.I.changeScreen(AppScreen.GamesActivities)}>
                                    More games
                                    <i className="bi bi-chevron-right ps-1"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                }
            </>
        );
    }
}